import { differenceInSeconds } from "date-fns";
import _ from "lodash";
import { CateringOrder } from "../../../shared/models/CateringOrder";

export enum SortKeys {
  UPDATED_AT = "updatedDateTime",
  FULFILLMENT_DATE = "fulfillmentDate",
}

export const parseFulfillmentTimes = (
  timeString: string | undefined,
  baseDate: Date
): { start: Date | null; end: Date | null } => {
  const clean = (timeString || "").trim();
  if (!clean) return { start: null, end: null };

  // Range: e.g. "11:00 AM - 12:00 PM"
  const rangeMatch = clean.match(/(\d{1,2}:\d{2}\s?(AM|PM))\s*-\s*(\d{1,2}:\d{2}\s?(AM|PM))/i);
  if (rangeMatch) {
    const startStr = rangeMatch[1];
    const endStr = rangeMatch[3];
    const start = new Date(baseDate.toDateString() + " " + startStr);
    const end = new Date(baseDate.toDateString() + " " + endStr);
    return {
      start: isNaN(start.getTime()) ? null : start,
      end: isNaN(end.getTime()) ? null : end,
    };
  }

  // Single time: e.g. "2:00 PM"
  const singleMatch = clean.match(/(\d{1,2}:\d{2}\s?(AM|PM))/i);
  if (singleMatch) {
    const dt = new Date(baseDate.toDateString() + " " + singleMatch[1]);
    return { start: isNaN(dt.getTime()) ? null : dt, end: null };
  }

  return { start: null, end: null };
};

export const getFulfillmentDateTime = (order: CateringOrder): Date | null => {
  if (!order || !order.fulfillmentDate) return null;

  const fDate = new Date(order.fulfillmentDate);
  if (isNaN(fDate.getTime())) return null;

  const parsed = parseFulfillmentTimes(order.fulfillmentTime, fDate);
  // Prefer start time for display/sorting; fall back to date-only
  if (parsed.start) return parsed.start;
  return fDate;
};

// Used when deciding whether an order is 'past' or still upcoming. For ranges
// we want to use the end time as the threshold so "11:00 AM - 12:00 PM" is
// considered past only after 12:00 PM.
export const getFilteringDateTime = (order: CateringOrder): Date | null => {
  if (!order || !order.fulfillmentDate) return null;
  const fDate = new Date(order.fulfillmentDate);
  if (isNaN(fDate.getTime())) return null;

  const parsed = parseFulfillmentTimes(order.fulfillmentTime, fDate);
  if (parsed.end) return parsed.end;
  if (parsed.start) return parsed.start;
  return fDate;
};

export const sortArray = (
  arr: any[],
  sortBy: SortKeys,
  ascending: boolean,
  includePast = false
) => {
  // hide orders whose fulfillment datetime is more than 1 hour in the past
  const ONE_HOUR_MS = 60 * 60 * 1000;
  const threshold = new Date(Date.now() - ONE_HOUR_MS);

  const filtered = _.cloneDeep(arr).filter((o) => {
    if (includePast) return true;
    const fdt = getFilteringDateTime(o);
    // keep orders without a valid fulfillment date/time
    if (!fdt) return true;
    return fdt.getTime() >= threshold.getTime();
  });

  const sorted = filtered.sort((a, b) => {
    const d1 =
      sortBy === SortKeys.UPDATED_AT
        ? new Date(a[sortBy])
        : getFulfillmentDateTime(a);
    const d2 =
      sortBy === SortKeys.UPDATED_AT
        ? new Date(b[sortBy])
        : getFulfillmentDateTime(b);

    // if either date is missing, treat missing as 0 difference so they stay stable
    if (!d1 && !d2) return 0;
    if (!d1) return ascending ? -1 : 1;
    if (!d2) return ascending ? 1 : -1;

    const diff = differenceInSeconds(d1 as Date, d2 as Date);
    return ascending ? diff : -diff;
  });

  return sorted;
};
